import type {
  ArticleDetailDTO,
  ArticleDTO,
  GlobeDataResponse,
  GlobePoint,
  PillarSummary,
  RegionSummary,
} from './types';

/**
 * Empty in development: Vite proxies /api to the Worker on :8787.
 * In production set VITE_API_BASE_URL to the Worker origin at build time.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/** Absolute API origin, for links in the footer. */
export const API_ORIGIN = BASE || (typeof window === 'undefined' ? '' : window.location.origin);

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      signal,
      headers: { Accept: 'application/json' },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('Network unreachable — is the API running on :8787?', 0);
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Non-JSON error body: keep the status-based message.
    }
    throw new ApiError(message, response.status);
  }

  return (await response.json()) as T;
}

export interface NewsQuery {
  region?: string | null;
  pillar?: string | null;
  subsection?: string | null;
  locale?: string | null;
  sort?: 'latest' | 'popular';
  limit?: number;
  signal?: AbortSignal;
}

export function fetchNews({
  region,
  pillar,
  subsection,
  locale,
  sort,
  limit,
  signal,
}: NewsQuery = {}): Promise<ArticleDTO[]> {
  const params = new URLSearchParams();
  if (region) params.set('region', region);
  if (pillar) params.set('pillar', pillar);
  if (subsection) params.set('subsection', subsection);
  if (locale) params.set('locale', locale);
  if (sort === 'popular') params.set('sort', 'popular');
  if (limit) params.set('limit', String(limit));
  const query = params.toString();
  return request<ArticleDTO[]>(`/api/v1/news${query ? `?${query}` : ''}`, signal);
}

export function fetchRegions(signal?: AbortSignal): Promise<RegionSummary[]> {
  return request<RegionSummary[]>('/api/v1/regions', signal);
}

export function fetchPillars(signal?: AbortSignal): Promise<PillarSummary[]> {
  return request<PillarSummary[]>('/api/v1/pillars', signal);
}

/** Also increments the view counter server-side, so call it once per open. */
export function fetchArticle(
  id: string,
  options: { locale?: string | null; count?: boolean; signal?: AbortSignal } = {},
): Promise<ArticleDetailDTO> {
  const params = new URLSearchParams();
  if (options.locale) params.set('locale', options.locale);
  if (options.count === false) params.set('count', '0');
  const query = params.toString();
  return request<ArticleDetailDTO>(
    `/api/v1/news/${encodeURIComponent(id)}${query ? `?${query}` : ''}`,
    options.signal,
  );
}

export function fetchGlobeData(signal?: AbortSignal): Promise<GlobePoint[]> {
  return request<GlobeDataResponse>('/api/v1/globe-data', signal);
}

